import * as Sentry from '@sentry/vue';
import type { Environment } from '@sparklayer-io/spark-dash-pages';
import { isAxiosError } from 'axios';
import mixpanel from 'mixpanel-browser';
import { defineStore } from 'pinia';
import notificationStore from '@/store/modules/notifications';
import { resetStores } from '@/store/reset';
import api from '@/utils/api';

type User = {
  user: {
    id: number;
    name: string;
    email: string;
    is_spark_staff: number;
  };
  autoLoginRestrictedSession: boolean;
  currentTeam: {
    id: number;
    name: string;
    is_partner: boolean;
    slugged_name: string;
    website: string;
    app_registration_source: string;
    billing_plan_name: string | null;
    shopify_recurring_trial_ends_at: string | null;
    shopify_enterprise_billing: boolean;
    shopify_enterprise_planned_go_live: string | null;
    billing_custom_price: number | null;
    orders_allowed: number | null;
    shopify_enterprise_billing_no_of_teams_allowed: number | null;
    billing_linked_team_id: number | null;
    shopify_enterprise_billing_completed: number;
    sales_agents_allowed: number | null;
    billing_duration: string | null;
    billing_provider: string;
    advanced_features_enabled: boolean;
    enterprise_features_enabled: boolean;
    billing_plan_data: Record<string, unknown> | null;
    billing_linked_site_id: number | null;
    is_scheduled_for_deletion: boolean;
  };
  intercomOptions: {
    app_id: string;
    name: string;
    email: string;
    user_id: number;
    is_staff: number;
    company: unknown;
    user_hash: string;
  };
  permissions: {
    updateBilling: boolean;
    manageAccount: boolean;
  };
  twoFactor: {
    enabled: boolean;
    required: boolean;
  };
  availableTeams: Team[];
};

type Team = {
  id: number;
  name: string;
  site_id: string;
  is_partner: boolean;
  deletion_scheduled_for?: string | null;
};

type State = {
  hasUserBeenLoaded: boolean;
  data: User | null;
  /** The user the rest of the app's state was loaded for, see `loadUser`. */
  sessionUserId: number | null;
  availableTeamsAdvanced: Team[];
  pendingSearches: number;
};

export default defineStore('auth', {
  state: (): State => ({
    hasUserBeenLoaded: false,
    data: null,
    sessionUserId: null,
    availableTeamsAdvanced: [],
    pendingSearches: 0,
  }),
  getters: {
    isLoggedIn: (state) => !!state.data?.user || false,
    user: (state) => state.data?.user,
    autoLoginRestrictedSession: (state) => state.data?.autoLoginRestrictedSession,
    name: (state) => state.data?.user?.name,
    firstName: (state) => state.data?.user?.name?.split(' ')[0] ?? state.data?.user?.name,
    isSparkStaff: (state) => state.data?.user?.is_spark_staff || false,
    email: (state) => state.data?.user?.email,
    acronymName: (state) =>
      state.data?.user?.name?.match(/\b\w/g)?.join('').toUpperCase().substring(0, 2),
    availableTeams: (state) => state.data?.availableTeams || [],
    isSearchingTeams: (state) => state.pendingSearches > 0,
    hasPermission: (state) => (permission: keyof User['permissions']) =>
      state.data?.permissions?.[permission] || false,
    twoFactorEnabled: (state) => state.data?.twoFactor?.enabled || false,
    twoFactorRequired: (state) => state.data?.twoFactor?.required || false,
    /**
     * The environment a site defaults to when the url doesn't name one. Partner teams only have a
     * test environment, everyone else lands on live.
     *
     * Undefined for a site this session doesn't know - spark staff browsing someone else's - so
     * the caller picks a default and lets `loadEnv` fall back if it guessed wrong.
     */
    defaultSiteEnv:
      (state) =>
      (siteId: string): Environment | undefined => {
        const currentTeam = state.data?.currentTeam;
        const isPartner =
          currentTeam?.slugged_name === siteId
            ? currentTeam.is_partner
            : state.data?.availableTeams?.find((team) => team.site_id === siteId)?.is_partner;

        if (isPartner === undefined) {
          return undefined;
        }

        return isPartner ? 'test' : 'live';
      },
  },
  actions: {
    setData(data: User) {
      this.data = data;
      this.hasUserBeenLoaded = true;
    },
    async loadUser(forceLoadUser?: boolean) {
      if (this.hasUserBeenLoaded && !forceLoadUser) {
        return;
      }

      try {
        const { data } = await api.get('/user');
        this.data = data;
        this.hasUserBeenLoaded = true;
      } catch (err) {
        console.error(err);
        this.data = null;
        // A refused request answers the question; one that never arrived doesn't, so leave it
        // unloaded and let the next caller ask again.
        this.hasUserBeenLoaded = isAxiosError(err) && err.response !== undefined;
      }

      const userId = this.data?.user?.id ?? null;

      // One page load holds one session's data. When the user behind it changes - the session
      // expired, or someone signed in in another tab or in place here - every other store still
      // holds the previous user's team, which `logout` never got the chance to clear.
      if (this.sessionUserId !== userId) {
        resetStores({ except: ['auth'] });
        this.sessionUserId = userId;
      }

      if (this.data) this.initAnalytics(this.data);
    },
    async logout() {
      try {
        await api.post('/logout');
      } catch (err) {
        console.error(err);
      }

      this.resetAnalytics();
      // Every store, not just this one: the previous team also lives in `env` (which the url is
      // built from) and `settings`.
      resetStores();
      // Not a Pinia store, so `resetStores` doesn't reach it - and the previous session's
      // notifications have no business following the user to the login screen.
      notificationStore.$reset();
    },
    initAnalytics(data: User) {
      try {
        if (data.currentTeam) {
          if (data.currentTeam.slugged_name) {
            Sentry.setTag('site-id', data.currentTeam.slugged_name);
          }
        }
        // Intercom Setup
        if (data?.intercomOptions) {
          window.Intercom('boot', data.intercomOptions);
        }
        // Hubspot setup
        if (data?.user && !data.user.is_spark_staff) {
          window._hsq = window._hsq || [];
          const _hsq = window._hsq;
          _hsq.push(['identify', { email: data.user.email }]);
        }
        // Mixpanel
        if (data?.user && !data.user.is_spark_staff) {
          try {
            mixpanel.identify(String(data.user.id));
          } catch {
            // Silently fail if Mixpanel is not initialised.
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    /** Undo {@link initAnalytics}, so the next user on this page load starts as themselves. */
    resetAnalytics() {
      try {
        Sentry.setTag('site-id', undefined);
        // Intercom holds the signed out user's conversations in its own cookie until it is shut
        // down, so without this the next user booted here inherits them.
        window.Intercom?.('shutdown');
        try {
          mixpanel.reset();
        } catch {
          // Silently fail if Mixpanel is not initialised.
        }
      } catch (err) {
        console.error(err);
      }
    },
    async changeSite(teamId: string) {
      try {
        await api.post('/change-site', { team_id: teamId });
      } catch (err) {
        const response = isAxiosError(err) ? err.response : undefined;
        switch (response?.status) {
          case 403:
            notificationStore.notify(
              'Error',
              response?.data?.message || `Sorry, you don't have permission to access this site.`,
              'error',
            );
            break;
          case 404:
            notificationStore.notify(
              'Error',
              `Sorry, we couldn't find the site you're looking for. Please try again.`,
              'error',
            );
            break;
        }
      }

      await this.loadUser(true);

      // Everything outside `auth` was loaded for the team being left behind.
      resetStores({ except: ['auth'] });
    },
    async getAvailableTeams() {
      if (this.data) {
        const { data } = await api.get('/available-teams');
        this.data.availableTeams = data ?? [];
      }
    },
    async searchAvailableTeamsAdvanced(search: string) {
      if (search === '') {
        this.availableTeamsAdvanced = [];
        return;
      }

      this.pendingSearches++;
      try {
        const { data } = await api.get('/search-available-teams-advanced', {
          params: { search },
        });
        this.availableTeamsAdvanced = data ?? [];
      } finally {
        this.pendingSearches--;
      }
    },
  },
});
